import React, { useState, useRef, useEffect } from 'react';
import { t } from 'i18next';
import { getCategories } from '../../../api/getCategories';

function BulkEditModal({ show, onClose }) {
    const [mode, setMode] = useState('select'); // 'select', 'table', 'upload'
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [announcements, setAnnouncements] = useState([]);
    const [editedAnnouncements, setEditedAnnouncements] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (show) {
            loadCategories();
            // Reset state when modal opens
            setMode('select');
            setSelectedCategory('');
            setSelectedSubcategory('');
            setSubcategories([]);
            setAnnouncements([]);
            setEditedAnnouncements({});
            setSelectedFile(null);
        }
    }, [show]);

    const loadCategories = async () => {
        try {
            const data = await getCategories();
            setCategories(data || []);
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);
        setSelectedSubcategory('');
        const category = categories.find(c => c.category_id === parseInt(categoryId) || c.id === parseInt(categoryId));
        setSubcategories(category?.subcategories || []);
    };

    // Step 1: Fetch announcements from server
    const handleFetchAnnouncements = async () => {
        if (!selectedCategory) {
            alert(t('pleaseSelectCategory') || 'Санатты таңдаңыз');
            return;
        }

        setLoading(true);
        try {
            // TODO: Replace with actual API endpoint
            // const response = await fetchBulkAnnouncements({
            //     category_id: selectedCategory,
            //     subcategory_id: selectedSubcategory || undefined
            // });

            // Mock data for now
            await new Promise(resolve => setTimeout(resolve, 1000));
            const mockData = [
                { id: 1, title: 'Тест жарнама 1', price: 10000, description: 'Сипаттама 1' },
                { id: 2, title: 'Тест жарнама 2', price: 20000, description: 'Сипаттама 2' },
            ];

            setAnnouncements(mockData);
            setMode('table');
        } catch (error) {
            console.error('Failed to fetch announcements:', error);
            alert(error.message || t('fetchFailed') || 'Жүктеу сәтсіз аяқталды');
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Download announcements as Excel
    const handleDownloadExcel = () => {
        // TODO: Generate and download Excel file
        const data = announcements.map(a => ({
            ID: a.id,
            Title: a.title,
            Price: a.price,
            Description: a.description
        }));

        // Convert to CSV for now (can be replaced with xlsx library)
        const csv = convertToCSV(data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `announcements_${selectedCategory}_${Date.now()}.csv`;
        link.click();
    };

    const convertToCSV = (data) => {
        if (data.length === 0) return '';
        const headers = Object.keys(data[0]);
        const rows = data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','));
        return [headers.join(','), ...rows].join('\n');
    };

    // Step 3: Edit in table
    const handleCellEdit = (id, field, value) => {
        setEditedAnnouncements(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value }
        }));
    };

    // Step 4: Save changes to server
    const handleSaveChanges = async () => {
        setLoading(true);
        try {
            const updates = Object.entries(editedAnnouncements).map(([id, changes]) => ({
                id: parseInt(id),
                ...changes
            }));

            // TODO: Replace with actual API endpoint
            // await saveBulkAnnouncements(updates);

            await new Promise(resolve => setTimeout(resolve, 1000));
            alert(t('changesSaved') || 'Өзгерістер сақталды');
            handleClose();
        } catch (error) {
            console.error('Failed to save changes:', error);
            alert(error.message || t('saveFailed') || 'Сақтау сәтсіз аяқталды');
        } finally {
            setLoading(false);
        }
    };

    // Step 5: Upload edited Excel
    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.csv'))) {
            setSelectedFile(file);
        } else if (file) {
            alert(t('onlyXlsxAllowed') || 'Тек .xlsx файлы қабылданады');
        }
    };

    const handleUploadFile = async () => {
        if (!selectedFile) {
            alert(t('pleaseSelectFile') || 'Файлды таңдаңыз');
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            // TODO: Replace with actual API endpoint
            // await uploadBulkAnnouncements(formData);

            await new Promise(resolve => setTimeout(resolve, 1000));
            alert(t('fileUploadedSuccess') || 'Файл сәтті жүктелді');
            handleClose();
        } catch (error) {
            console.error('Upload failed:', error);
            alert(error.message || t('uploadFailed') || 'Жүктеу сәтсіз аяқталды');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setMode('select');
        setSelectedCategory('');
        setSelectedSubcategory('');
        setSubcategories([]);
        setAnnouncements([]);
        setEditedAnnouncements({});
        setSelectedFile(null);
        onClose();
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

            {/* Modal */}
            <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${mode === 'table' ? 'max-w-4xl' : 'max-w-md'} max-h-[90vh] overflow-hidden flex flex-col`}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                        {mode === 'table' ? (t('editAnnouncements') || 'Жарнамаларды өңдеу') : (t('bulkEdit') || 'Массивті редакциялау')}
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-6">
                    {mode === 'select' && (
                        <div className="space-y-5">
                            {/* Category Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('selectCategory') || 'Санатты таңдау'} *
                                </label>
                                <select
                                    value={selectedCategory}
                                    onChange={(e) => handleCategoryChange(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-[#147F26] transition-colors text-sm"
                                >
                                    <option value="">{t('selectCategory') || 'Санатты таңдаңыз'}</option>
                                    {categories.map((category) => (
                                        <option
                                            key={category.category_id || category.id}
                                            value={category.category_id || category.id}
                                        >
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Subcategory Selection */}
                            {subcategories.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('selectSubcategory') || 'Субсанатты таңдау'}
                                    </label>
                                    <select
                                        value={selectedSubcategory}
                                        onChange={(e) => setSelectedSubcategory(e.target.value)}
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-[#147F26] transition-colors text-sm"
                                    >
                                        <option value="">{t('selectSubcategory') || 'Субсанатты таңдаңыз'}</option>
                                        {subcategories.map((sub) => (
                                            <option key={sub.subcategory_id || sub.id} value={sub.subcategory_id || sub.id}>
                                                {sub.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="pt-4 space-y-3">
                                <button
                                    onClick={handleFetchAnnouncements}
                                    disabled={loading || !selectedCategory}
                                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#147F26] hover:bg-[#0f5f1a] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-xl transition-colors"
                                >
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            {t('loading') || 'Жүктелуде...'}
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                            </svg>
                                            {t('getAnnouncements') || 'Жарнамаларды алу'}
                                        </>
                                    )}
                                </button>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-gray-200"></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white text-gray-500">{t('or') || 'немесе'}</span>
                                    </div>
                                </div>

                                {/* File Upload for already edited file */}
                                <div
                                    onClick={() => fileInputRef.current?.click()}
                                    className="border-2 border-dashed border-gray-300 hover:border-[#147F26] hover:bg-gray-50 rounded-xl p-4 cursor-pointer transition-colors"
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx,.csv"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                    <div className="text-center">
                                        <svg className="mx-auto h-8 w-8 mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        {selectedFile ? (
                                            <p className="text-sm font-medium text-[#147F26]">{selectedFile.name}</p>
                                        ) : (
                                            <p className="text-sm text-gray-600">{t('uploadEditedFile') || 'Өңделген файлды жүктеу'}</p>
                                        )}
                                    </div>
                                </div>

                                {selectedFile && (
                                    <button
                                        onClick={handleUploadFile}
                                        disabled={loading}
                                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-xl transition-colors"
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                </svg>
                                                {t('uploading') || 'Жүктелуде...'}
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                </svg>
                                                {t('uploadToServer') || 'Серверге жүктеу'}
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {mode === 'table' && (
                        <div className="space-y-4">
                            {/* Action Buttons */}
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleDownloadExcel}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-xl transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    {t('downloadExcel') || 'Excel жүктеу'}
                                </button>
                            </div>

                            {/* Table */}
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-medium text-gray-700">ID</th>
                                            <th className="px-4 py-3 text-left font-medium text-gray-700">{t('title') || 'Тақырып'}</th>
                                            <th className="px-4 py-3 text-left font-medium text-gray-700">{t('price') || 'Бағасы'}</th>
                                            <th className="px-4 py-3 text-left font-medium text-gray-700">{t('description') || 'Сипаттама'}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {announcements.map((announcement) => (
                                            <tr key={announcement.id}>
                                                <td className="px-4 py-3 text-gray-500">{announcement.id}</td>
                                                <td className="px-4 py-3">
                                                    <input
                                                        type="text"
                                                        value={editedAnnouncements[announcement.id]?.title ?? announcement.title}
                                                        onChange={(e) => handleCellEdit(announcement.id, 'title', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-200 rounded focus:border-[#147F26] focus:outline-none"
                                                    />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <input
                                                        type="number"
                                                        value={editedAnnouncements[announcement.id]?.price ?? announcement.price}
                                                        onChange={(e) => handleCellEdit(announcement.id, 'price', parseInt(e.target.value) || 0)}
                                                        className="w-full px-2 py-1 border border-gray-200 rounded focus:border-[#147F26] focus:outline-none"
                                                    />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <input
                                                        type="text"
                                                        value={editedAnnouncements[announcement.id]?.description ?? announcement.description}
                                                        onChange={(e) => handleCellEdit(announcement.id, 'description', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-200 rounded focus:border-[#147F26] focus:outline-none"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {mode === 'table' && (
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
                        <button
                            onClick={() => setMode('select')}
                            className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
                        >
                            {t('back') || 'Артқа'}
                        </button>
                        <button
                            onClick={handleSaveChanges}
                            disabled={loading || Object.keys(editedAnnouncements).length === 0}
                            className="flex items-center gap-2 px-6 py-2.5 bg-[#147F26] hover:bg-[#0f5f1a] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-xl transition-colors"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    {t('saving') || 'Сақталуда...'}
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t('saveChanges') || 'Өзгерістерді сақтау'}
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default BulkEditModal;
